import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  Divider,
  styled,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import SchoolIcon from "@mui/icons-material/School";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ListItemImages from "../../components/Item/ListItemImages";
import { PageLayout } from "../../components/PageLayout/PageLayout";
import Seo from "../../components/Seo/Seo";
import { SEO_DEFAULTS } from "../../seo/seoConstants";
import { CommonBoxStyles, WhatsAppButton } from "../shared/styledComponents";
import { EDUCATION_PAGE_TEXTS } from "../shared/constants";
import { EDUCATION_WHATSAPP } from "../../config";
import { gaEvent } from "../../analytics";
import useUpdateAuthToken from "../../hooks/useUpdateAuthToken";
import {
  useGetEducationItemBySlugQuery,
  useGetEnrollmentsQuery,
  useCreateEnrollmentMutation,
} from "../../services/education/education";
import type { RootState } from "../../app/store";
import type { EducationItem } from "../../types/types";

const hasEducationItemImages = (educationItem: EducationItem) =>
  Boolean(educationItem.picture) ||
  (Array.isArray(educationItem.images) && educationItem.images.some(Boolean));

const DetailGrid = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(3),
  width: "100%",
}));

const DetailContainer = styled(Box)(({ theme }) => ({
  background: theme.palette.background.paper,
  width: "100%",
  display: "flex",
  borderRadius: theme.spacing(1),
}));

const StyledDivider = styled(Divider)(({ theme }) => ({
  borderColor: theme.palette.divider,
}));

const getCategoryLabel = (category: EducationItem["category"]) =>
  category === "program"
    ? EDUCATION_PAGE_TEXTS.categoryProgram
    : EDUCATION_PAGE_TEXTS.categoryCourse;

interface EducationDetailContentProps {
  educationItem: EducationItem;
  isAuthenticated: boolean;
  isEnrolled: boolean;
  isEnrolling: boolean;
  onEnroll: () => void;
  whatsappUrl: string | null;
}

const EducationItemName: React.FC<{ educationItem: EducationItem }> = ({ educationItem }) => (
  <Box display="flex" alignItems="center" gap={1} mb={1} flexWrap="wrap">
    <SchoolIcon color="primary" fontSize="large" />
    <Typography variant="h4" fontWeight="bold">
      {educationItem.name}
    </Typography>
    <Chip label={getCategoryLabel(educationItem.category)} color="primary" />
  </Box>
);

const EducationItemDetails: React.FC<{ educationItem: EducationItem }> = ({ educationItem }) => (
  <Box display="flex" gap={3} flexWrap="wrap">
    {educationItem.duration && (
      <Typography variant="body2" color="text.secondary">
        {EDUCATION_PAGE_TEXTS.duration}: {educationItem.duration}
      </Typography>
    )}
    {educationItem.start_date && educationItem.end_date && (
      <Typography variant="body2" color="text.secondary">
        {EDUCATION_PAGE_TEXTS.dates}: {educationItem.start_date} — {educationItem.end_date}
      </Typography>
    )}
    {educationItem.max_capacity > 0 && (
      <Typography variant="body2" color="text.secondary">
        {EDUCATION_PAGE_TEXTS.capacity}: {educationItem.max_capacity}
      </Typography>
    )}
  </Box>
);

const EducationItemActions: React.FC<
  Pick<
    EducationDetailContentProps,
    "isAuthenticated" | "isEnrolled" | "isEnrolling" | "onEnroll" | "whatsappUrl"
  >
> = ({ isAuthenticated, isEnrolled, isEnrolling, onEnroll, whatsappUrl }) => (
  <Box display="flex" flexWrap="wrap" alignItems="center" gap={1}>
    {isAuthenticated &&
      (isEnrolled ? (
        <Chip label={EDUCATION_PAGE_TEXTS.alreadyEnrolled} color="success" />
      ) : (
        <WhatsAppButton
          onClick={onEnroll}
          disabled={isEnrolling}
          sx={{
            backgroundColor: "primary.main",
            "&:hover": { backgroundColor: "secondary.main" },
            ml: 0,
          }}
        >
          {isEnrolling ? EDUCATION_PAGE_TEXTS.enrolling : EDUCATION_PAGE_TEXTS.enrollButton}
        </WhatsAppButton>
      ))}
    {whatsappUrl && (
      <a
        href={whatsappUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => gaEvent("whatsapp_click", { source: "education_enroll" })}
      >
        <WhatsAppButton startIcon={<WhatsAppIcon />}>
          {EDUCATION_PAGE_TEXTS.whatsappButton}
        </WhatsAppButton>
      </a>
    )}
  </Box>
);

const DesktopDetail: React.FC<EducationDetailContentProps> = ({
  educationItem,
  isAuthenticated,
  isEnrolled,
  isEnrolling,
  onEnroll,
  whatsappUrl,
}) => (
  <DetailContainer>
    <DetailGrid py={1}>
      {hasEducationItemImages(educationItem) && (
        <Box
          p={2}
          flexShrink={0}
          sx={{
            maxWidth: 360,
            "& img": { objectFit: "contain", borderRadius: 2 },
          }}
        >
          <ListItemImages item={educationItem} />
        </Box>
      )}
      <Box flex={1} p={2} display="flex" flexDirection="column" justifyContent="space-between">
        <Box>
          <EducationItemName educationItem={educationItem} />

          {educationItem.description && (
            <>
              <StyledDivider sx={{ my: 1 }} />
              <Typography variant="h6" fontWeight="bold">
                Descripción:
              </Typography>
              <Typography variant="body1">{educationItem.description}</Typography>
            </>
          )}

          <StyledDivider sx={{ my: 1 }} />
          <EducationItemDetails educationItem={educationItem} />
        </Box>

        <Box mt={2}>
          <StyledDivider sx={{ mb: 1 }} />
          <EducationItemActions
            isAuthenticated={isAuthenticated}
            isEnrolled={isEnrolled}
            isEnrolling={isEnrolling}
            onEnroll={onEnroll}
            whatsappUrl={whatsappUrl}
          />
        </Box>
      </Box>
    </DetailGrid>
  </DetailContainer>
);

const MobileDetail: React.FC<EducationDetailContentProps> = ({
  educationItem,
  isAuthenticated,
  isEnrolled,
  isEnrolling,
  onEnroll,
  whatsappUrl,
}) => (
  <DetailContainer>
    <Box width="100%">
      <Box p={2}>
        <EducationItemName educationItem={educationItem} />
      </Box>

      {hasEducationItemImages(educationItem) && (
        <Box px={2} sx={{ "& img": { objectFit: "contain", borderRadius: 2 } }}>
          <ListItemImages item={educationItem} />
        </Box>
      )}

      <Box p={2}>
        {educationItem.description && (
          <>
            <StyledDivider sx={{ mb: 1 }} />
            <Typography variant="body1">{educationItem.description}</Typography>
          </>
        )}

        <StyledDivider sx={{ my: 1 }} />
        <EducationItemDetails educationItem={educationItem} />

        <StyledDivider sx={{ my: 1 }} />
        <EducationItemActions
          isAuthenticated={isAuthenticated}
          isEnrolled={isEnrolled}
          isEnrolling={isEnrolling}
          onEnroll={onEnroll}
          whatsappUrl={whatsappUrl}
        />
      </Box>
    </Box>
  </DetailContainer>
);

const EducationDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { isAuthenticated } = useAuth0();
  useUpdateAuthToken();
  const token = useSelector((state: RootState) => state.auth.token);
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    data: educationItem,
    isLoading,
    isError,
  } = useGetEducationItemBySlugQuery(slug ?? "", { skip: !slug });

  const { data: enrollments } = useGetEnrollmentsQuery(undefined, {
    skip: !isAuthenticated || !token,
  });
  const [createEnrollment, { isLoading: isEnrolling }] = useCreateEnrollmentMutation();

  const [snackbar, setSnackbar] = React.useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  React.useEffect(() => {
    if (educationItem) {
      gaEvent("view_item", { item_category: "education", item_name: educationItem.name });
    }
  }, [educationItem]);

  const isEnrolled = React.useMemo(
    () => enrollments?.some((e) => e.education_item_id === String(educationItem?.id)) ?? false,
    [enrollments, educationItem],
  );

  const handleEnroll = async () => {
    if (!isAuthenticated || !token || !educationItem) return;
    try {
      gaEvent("education_enroll", { course_name: educationItem.name, method: "whatsapp" });
      await createEnrollment({ education_item: educationItem.id }).unwrap();
      setSnackbar({ open: true, message: EDUCATION_PAGE_TEXTS.enrollSuccess, severity: "success" });
    } catch {
      setSnackbar({ open: true, message: EDUCATION_PAGE_TEXTS.enrollError, severity: "error" });
    }
  };

  const whatsappUrl = EDUCATION_WHATSAPP
    ? `https://wa.me/${EDUCATION_WHATSAPP}?text=${encodeURIComponent(EDUCATION_PAGE_TEXTS.whatsappMessage)}`
    : null;

  const DetailComponent = isSmall ? MobileDetail : DesktopDetail;

  return (
    <PageLayout>
      {educationItem && (
        <Seo
          title={`${educationItem.name} | Educación - ${SEO_DEFAULTS.siteName}`}
          description={educationItem.description || undefined}
          canonical={`${SEO_DEFAULTS.baseUrl}/educacion/${slug}`}
        />
      )}
      <Box p={2}>
        <Link to="/educacion" style={{ textDecoration: "none" }}>
          <Box display="flex" alignItems="center" gap={0.5} mb={2}>
            <ArrowBackIcon color="primary" fontSize="small" />
            <Typography variant="body2" color="primary">
              {EDUCATION_PAGE_TEXTS.backToList}
            </Typography>
          </Box>
        </Link>

        {isLoading ? (
          <CommonBoxStyles>
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
              <Typography ml={2}>{EDUCATION_PAGE_TEXTS.coursesLoading}</Typography>
            </Box>
          </CommonBoxStyles>
        ) : isError ? (
          <Alert severity="error">{EDUCATION_PAGE_TEXTS.detailError}</Alert>
        ) : !educationItem ? (
          <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
            {EDUCATION_PAGE_TEXTS.detailNotFound}
          </Typography>
        ) : (
          <DetailComponent
            educationItem={educationItem}
            isAuthenticated={isAuthenticated}
            isEnrolled={isEnrolled}
            isEnrolling={isEnrolling}
            onEnroll={handleEnroll}
            whatsappUrl={whatsappUrl}
          />
        )}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageLayout>
  );
};

export default EducationDetailPage;
